import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { login } from '../services/authService';
import {
  Container,
  BackgroundAnimation,
  FactoryFloorIframe,
  GridOverlay,
  HexagonPattern,
  LoginContainer,
  GlowEffect,
  BrandSection,
  BrandTitle,
  BrandTagline,
  CircleDecoration,
  LoginSection,
  LoginTitle,
  LoginSubtitle,
  BiometricContainer,
  TabContainer,
  Tab,
  Form,
  InputGroup,
  Label,
  Input,
  Button,
  StatusText,
  Footer,
  Copyright,
  TwoFactorModal,
  TwoFactorCard,
  TwoFactorTitle,
  TwoFactorDescription,
  DataStream,
  DataLine,
  DigitalLogo,
  LogoRing,
  LogoInnerCircle,
  LogoDot,
  LogoLine,
  OauthProviders
} from '../styles/LoginStyles';
import { fetchOAuthConfigs, processOAuthCallback } from '../services/oauthService';

import { useMsal } from "@azure/msal-react";
import { loginRequest } from '../authConfig';

const buildProviders = () => {
  const providers = [];

  const ENABLE_AZURE_LOGIN = process.env.REACT_APP_ENABLE_AZURE_LOGIN == "true";

  if (ENABLE_AZURE_LOGIN) {
    providers.push({ server_name: "Azure", message: "Sign in with Azure", icon: "/images/oauthConnectors/azure.svg" });
  }

  return providers;
};



const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { provider: urlProvider } = useParams();
  const [status, setStatus] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorMethod, setTwoFactorMethod] = useState('app');

  const [oauthProviders] = useState(buildProviders());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const APPLICATION_ID = 'BLUEMORMON';
  const APPLICATION_SOURCE = 'BLUEMORMON';
  const basePath = process.env.PUBLIC_URL || "";

  const { instance } = useMsal();


  const [dataLines, setDataLines] = useState(() => {
    return Array.from({ length: 15 }, (_, i) => ({
      id: i,
      text: Array.from({ length: Math.floor(Math.random() * 20) + 10 }, () =>
        Math.random() > 0.5 ? '1' : '0'
      ).join(''),
      x: Math.random() * 100,
      y: Math.random() * 100,
      speed: Math.random() * 10 + 5,
      delay: Math.random() * 5
    }));
  });
  const [authParams, setAuthParams] = useState({
    code: null,
    provider: null,
    state: null,
    applicationId: 'BLUEMORMON',
    applicationSource: 'BLUEMORMON'
  });

  // OAUTH
  // Parse URL parameters when redirected back from OAuth provider
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get('code');
    const state = queryParams.get('state');
    const provider = queryParams.get('provider') || localStorage.getItem('oauth_provider');

    if (code && state) {
      setAuthParams(prev => ({
        ...prev,
        code,
        provider,
        state
      }));
    }
  }, [location]);


  const handleProviderClick = (provider) => {
    instance.loginRedirect(loginRequest);
  };

  // Handle OAuth callback from provider

  // Handle OAuth callback
  useEffect(() => {
    // Check if this is an OAuth callback (has a provider parameter and a code)
    const isOAuthCallback = urlProvider && location.search.includes('code=');

    if (isOAuthCallback) {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');

      console.log('OAuth callback detected!', {
        provider: urlProvider,
        codeLength: code ? code.length : 0,
        state
      });

      if (code && state && state === localStorage.getItem('state')) {
        handleOAuthCallback(code, state, urlProvider);
      }
      else{
        setError('Authentication failed. Please try again.');
        navigate('/login');
      }
    }
  }, [urlProvider, location.search]);

  // Process the OAuth callback
  const handleOAuthCallback = async (code, state, provider) => {
    try {
      setIsLoading(true);
      const authData = await processOAuthCallback(
        code, state, provider, APPLICATION_ID, APPLICATION_SOURCE, localStorage.getItem('domainName'),localStorage.getItem('selected_oauth_server_name')
      );
      if (authData && authData.access_token) {
        // Store the token and user data
        localStorage.setItem('authToken', authData.access_token);
        // Redirect to dashboard or home page
        setTimeout(() => {
          navigate('/listing');
        }, 3000);
      } else {
        setError('Authentication failed. Please try again.');
        navigate('/login');
      }
    } catch (err) {
      setError('Authentication failed. Please try again.');
      navigate('/login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCallbackResponse = (detail) => {
    // Handle the OAuth response
    console.log('OAuth callback received:', detail);

    if (detail && detail.user && detail.user.token) {
      // Store the bearer token
      localStorage.setItem('auth_token', detail.user.token);

      // Redirect to dashboard or home page
      navigate('/dashboard');
    }
  };

  const handleProviderSelection = (detail) => {
    // When user clicks on an OAuth provider in the graph
    // console.log('Provider selected:', detail);
    if (detail && detail.provider) {
      localStorage.setItem('oauth_provider', detail.provider);
    }
  };

  // OAUTH END

  // Handle login form submission
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setStatus('Logging in...');
    try {
      // Call the login function from authService
      const response = await login(username, password);
      
      setStatus('Login successful!');
    const userRoles = response.roles;
      
      console.log(response)
      // Check if two-factor authentication is required
      if (response.requires_2fa) {
        setShowTwoFactor(true);
      } else {
        // If no 2FA required, redirect to dashboard
        setTimeout(() => {
          // navigate('/dashboard');
         userRoles?.includes('CLAIM_ASSESSOR') ? navigate('/workbench-listing') : navigate('/agent-studio-ai');
        }, 1000);
      }
    } catch (error) {
      console.error('Login failed:', error);
      setStatus(`Login failed: ${error.message || 'Please check your credentials'}`);
    }
  };

  useEffect(() => {
    console.log("Clearing cache and local storage...");
    // localStorage.clear(); // Clear all local storage items
    // sessionStorage.clear(); // Clear session storage if needed
    caches.keys().then((cacheNames) => {
      cacheNames.forEach((cacheName) => {
        caches.delete(cacheName); // Clear browser cache
      });
    });
  }, []);


  // Generate random data stream lines for the background
  useEffect(() => {
    const generateDataLines = () => {
      const lines = [];
      const characters = '01010101110000111101010111001010101010111110000';
      
      for (let i = 0; i < 50; i++) {
        const line = {
          id: i,
          text: '',
          x: Math.random() * 100,
          y: Math.random() * 100,
          speed: 0.5 + Math.random() * 2,
          delay: Math.random() * 5
        };
        
        // Generate random binary/hex text
        let text = '';
        for (let j = 0; j < 30; j++) {
          text += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        line.text = text;
        
        lines.push(line);
      }
      
      setDataLines(lines);
    };
    
    generateDataLines();
  }, []);







  const handleTwoFactorSubmitUpdated = (e) => {
    if (e) e.preventDefault();
    
    setStatus('Verifying code...');
    
    // Validate 2FA code
    if (twoFactorCode === '123456' || twoFactorCode.length === 6) {
      // Simulate verification process
      setTimeout(() => {
        setStatus('Code verified!');
        setTimeout(() => {
          setShowTwoFactor(false);
          setStatus('Login successful!');
          setTimeout(() => {
            const mockAuthResponse = {
              token: process.env.TWOFACTOR_MOCK_TOKEN,
              user: {
                id: 1,
                name: 'Test User'
              }
            };

            // Store the token in localStorage
            localStorage.setItem('authToken', mockAuthResponse.token);

            navigate('/listing');
          }, 1000);
        }, 1000);
      }, 1500);
    } else {
      setStatus('Invalid code. Please try again.');
    }
  };

  return (
    <>
      <Container style={{
        background:
          "radial-gradient(circle at 20% 80%, rgb(123, 108, 246) 0%, transparent 50%), radial-gradient(circle at 41% 20%, rgb(111, 168, 220) 0%, transparent 85%), radial-gradient(circle at 70% 30%, rgb(184, 198, 219) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgb(214, 222, 232) 0%, transparent 45%), #e6edf5",

        backgroundBlendMode: 'normal',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '550px',
          width: '100%',
          background: 'white',
          borderRadius: '16px',
          padding: '80px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              
              <h1 style={{ 
                fontSize: '1.5rem', 
                fontWeight: '600', 
                color: '#1f2937',
                margin: 0
              }}>
                Login
                {/* <img src="/images/logo/bmm.png" alt="Logo" style={{ height: '80px' }} /> */}
              </h1>
            </div>
          </div>

          <BiometricContainer>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              style={{ width: '100%' }}
            >
              <Form onSubmit={handleLoginSubmit}>
                <InputGroup>
                  <Label>User name</Label>
                  <Input
                    type="text"
                    placeholder="Enter email"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    style={{ 
                      background: '#ffffff',
                      border: '1px solid #e3e3e3',
                      padding: '0.75rem 1rem',
                      fontSize: '1.1rem'
                    }}
                  />
                </InputGroup>

                <InputGroup>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    style={{ 
                      background: '#ffffff',
                      border: '1px solid #e3e3e3',
                      padding: '0.75rem 1rem',
                      fontSize: '1.1rem',
                      letterSpaceing: '2px'
                    }}
                  />
                </InputGroup>

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '1.5rem',
                  fontSize: '0.875rem'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input type="checkbox" style={{ cursor: 'pointer' }} />
                    <span style={{ color: '#6b7280' }}>Remember me</span>
                  </label>
                  {/* <a href="#" style={{ color: '#00a8ff', textDecoration: 'none' }}>Forgot password?</a> */}
                  <a 
                    href="/forgot-password" 
                    style={{ color: '#00A1FF', textDecoration: 'none', cursor: 'pointer' }}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/forgot-password');
                    }}
                  >
                    Forgot password?
                  </a>
                </div>

                <Button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    background: 'var(--primary-500)',
                    width: '100%',
                    padding: '0.875rem',
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    borderRadius: '8px',
                    border: 'none',
                    marginBottom: '1rem'
                  }}
                >
                  Sign in
                </Button>
              </Form>

              {oauthProviders?.length > 0 && oauthProviders.map((provider) => {
                const providerMsg = provider?.message  || "Sign in with Google";
                const providerName = provider?.server_name || "Goolge";
                const providericon = provider?.icon || "https://www.google.com/favicon.ico";
                return (
                <button
                  onClick={() => handleProviderClick(oauthProviders[0])}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    color: '#4b5563',
                    fontWeight: '500',
                    marginBottom: '1.5rem'
                  }}
                >
                  <img 
                    src={`${basePath}${providericon}`}
                    alt={`${providerName}`}
                    width={18}
                    height={18}
                  />
                  {provider?.message || "Sign in with Google"}
                </button>
              )
              })}

              <div style={{ 
                textAlign: 'center', 
                fontSize: '0.875rem',
                color: '#6b7280'
              }}>
                Don't have an account? <a href="#" style={{ color: '#00a8ff', textDecoration: 'none', fontWeight: '500' }}>Sign up</a>
              </div>

              {status && (
                <StatusText style={{ marginTop: '1rem', textAlign: 'center' }}>{status}</StatusText>
              )}
            </motion.div>
          </BiometricContainer>
        </div>
      </Container>

      {/* Two-Factor Authentication Modal */}
      {showTwoFactor && (
        <TwoFactorModal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <TwoFactorCard
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <TwoFactorTitle>Two-Factor Authentication</TwoFactorTitle>
            <TwoFactorDescription>
              Please enter the verification code from your authenticator app or device.
            </TwoFactorDescription>

            <TabContainer style={{ marginBottom: '1.5rem' }}>
              <Tab
                active={twoFactorMethod === 'app' ? "true" : "false"}
                onClick={() => setTwoFactorMethod('app')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Authenticator App
              </Tab>
              <Tab
                active={twoFactorMethod === 'sms' ? "true" : "false"}
                onClick={() => setTwoFactorMethod('sms')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                SMS
              </Tab>
              <Tab
                active={twoFactorMethod === 'email' ? "true" : "false"}
                onClick={() => setTwoFactorMethod('email')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Email
              </Tab>
            </TabContainer>

            <Form onSubmit={handleTwoFactorSubmitUpdated} style={{ width: '100%' }}>
              <InputGroup>
                <Label>Verification Code</Label>
                <Input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  maxLength={6}
                  pattern="[0-9]*"
                  inputMode="numeric"
                  autoFocus
                />
              </InputGroup>

              <StatusText style={{ marginTop: '0.5rem', marginBottom: '1rem', textAlign: 'center' }}>
                {twoFactorMethod === 'app' && 'Open your authenticator app to view the code'}
                {twoFactorMethod === 'sms' && 'A code has been sent to your registered phone'}
                {twoFactorMethod === 'email' && 'A code has been sent to your registered email'}
              </StatusText>

              <Button
                type="submit"
                disabled={twoFactorCode.length !== 6}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ width: '100%' }}
              >
                Verify
              </Button>

              <Button
                type="button"
                onClick={() => setShowTwoFactor(false)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  width: '100%',
                  marginTop: '0.5rem',
                  background: 'transparent',
                  color: 'rgba(0, 102, 255, 0.8)',
                  border: '1px solid rgba(0, 102, 255, 0.3)'
                }}
              >
                Cancel
              </Button>
            </Form>
          </TwoFactorCard>
        </TwoFactorModal>
      )}
    </>
  );
};

export default Login;
